import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Waves,
  Trophy,
  Zap,
  Clock,
  Route,
  CheckCircle,
  User,
  LogOut,
  History,
  AlertTriangle
} from 'lucide-react';
import { readActiveSession, clearActiveSession, saveSessionWithRetry } from '@/lib/sessionSave';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DBPRO Brand Colors
const BRAND = {
  navyDark: '#0A1A2F',
  navy: '#1A2E4A',
  navyLight: '#243B5A',
  orange: '#FF6B35',
  orangeHover: '#E55A2B',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  mediumGray: '#9CA3AF',
  darkGray: '#6B7280',
  toggleActive: '#FF6B35',
  toggleInactive: '#9CA3AF'
};

// Logo URL
const DBPRO_LOGO_URL = 'https://customer-assets.emergentagent.com/job_gps-tracker-dash-1/artifacts/tln5whso_Final%20brand%20logo.png';

// Format duration from seconds to mm:ss
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Format distance
const formatDistance = (meters) => {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
};

const DISTANCE_OPTIONS = [
  { value: '200', label: '200m' },
  { value: '500', label: '500m' },
  { value: '1000', label: '1000m' },
  { value: '2000', label: '2000m' },
  { value: 'custom', label: 'Custom' }
];

const MODE_OPTIONS = [
  { value: 'practice', label: 'Practice Mode', icon: Waves },
  { value: 'race', label: 'Race Mode', icon: Trophy }
];

// Custom Toggle Component
const DBPROToggle = ({ checked, onCheckedChange }) => (
  <button
    onClick={() => onCheckedChange(!checked)}
    data-testid="auto-stop-switch"
    className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none cursor-pointer"
    style={{ backgroundColor: checked ? BRAND.toggleActive : BRAND.toggleInactive }}
    aria-checked={checked}
    role="switch"
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${checked ? 'translate-x-6' : 'translate-x-1'}`}
    />
  </button>
);

const TrainingDashboard_DBPRO = () => {
  const navigate = useNavigate();
  
  // Read auto-fill from localStorage (defaults: 500m, practice, auto-stop off)
  const autofill = (() => {
    try {
      const raw = localStorage.getItem('dbpro_autofill');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  const [mode, setMode] = useState(autofill?.mode || 'practice');
  const [targetDistance, setTargetDistance] = useState(autofill?.targetDistance || '500');
  const [customDistance, setCustomDistance] = useState(autofill?.customDistance || '');
  const [autoStopEnabled, setAutoStopEnabled] = useState(autofill?.autoStopEnabled || false);
  const [showCustomInput, setShowCustomInput] = useState(autofill?.targetDistance === 'custom');
  const [lastSession, setLastSession] = useState(null);
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [recoveredSession, setRecoveredSession] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState(null);

  // Detect an unfinished session snapshot (app was killed mid-session)
  useEffect(() => {
    const snap = readActiveSession();
    if (!snap) return;
    if ((snap.totalDistance || 0) < 5 && (snap.duration || 0) < 10) {
      clearActiveSession();
      return;
    }
    setRecoveredSession(snap);
  }, []);

  // Fetch user and last session
  useEffect(() => {
    const fetchData = async () => {
      try {
        let storedToken = localStorage.getItem('dbpro_session_token');
        let headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};

        // Fix 3: Refresh token on dashboard load
        try {
          if (storedToken) {
            const refreshResponse = await fetch(`${API}/auth/refresh`, {
              method: 'POST',
              headers
            });
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              if (refreshData.session_token) {
                storedToken = refreshData.session_token;
                localStorage.setItem('dbpro_session_token', storedToken);
                headers = { 'Authorization': `Bearer ${storedToken}` };
              }
            }
          }
        } catch (refreshErr) {
          console.warn('Token refresh failed:', refreshErr);
        }

        // Fix 2: Background retry of failed sessions
        const failedSessions = JSON.parse(localStorage.getItem('dbpro_failed_sessions') || '[]');
        if (failedSessions.length > 0 && storedToken) {
          const successfullyRetried = [];
          for (let i = 0; i < failedSessions.length; i++) {
            try {
              const { failed_at, created_at_local, ...sessionData } = failedSessions[i];
              const response = await fetch(`${API}/sessions`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(sessionData)
              });
              // Only mark as successful if server returned 2xx
              if (response.ok) {
                successfullyRetried.push(i);
              } else {
                console.error('Failed to retry session, server returned:', response.status);
              }
            } catch (err) {
              console.error('Failed to retry session:', err);
            }
          }
          if (successfullyRetried.length > 0) {
            const remainingFailed = failedSessions.filter((_, idx) => !successfullyRetried.includes(idx));
            localStorage.setItem('dbpro_failed_sessions', JSON.stringify(remainingFailed));
            console.log(`Synced ${successfullyRetried.length} failed session(s)`);
          }
        }

        // Fetch user
        const userResponse = await fetch(`${API}/auth/me`, { headers });
        
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
        
        // Fetch last session
        const response = await axios.get(`${API}/sessions/latest`, { headers });
        if (response.data) {
          setLastSession(response.data);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      const storedToken = localStorage.getItem('dbpro_session_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      await fetch(`${API}/auth/logout`, { method: 'POST', headers });
    } catch (err) {
      console.error('Logout error:', err);
    }
    // Clear localStorage
    localStorage.removeItem('dbpro_session_token');
    localStorage.removeItem('dbpro_user');
    navigate('/', { replace: true });
  };

  const handleViewHistory = () => {
    navigate('/history');
  };

  const handleDistanceChange = (value) => {
    setTargetDistance(value);
    setShowCustomInput(value === 'custom');
  };

  const handleRecoverSession = async () => {
    const snap = recoveredSession;
    if (!snap) return;
    setRecovering(true);
    const session = {
      mode: snap.mode || 'practice',
      target_distance: snap.targetDistance || 500,
      distance_covered: snap.totalDistance || 0,
      duration_seconds: snap.duration || 0,
      average_speed_ms: (snap.duration || 0) > 0 ? (snap.totalDistance || 0) / snap.duration : 0,
      max_speed_ms: snap.maxSpeed || 0,
      auto_stop_enabled: !!snap.autoStopEnabled,
      auto_stopped: false,
      coordinates: snap.coordinates || [],
      heat_number: snap.heatNumber || 1,
      created_at_local: snap.savedAt || new Date().toISOString()
    };
    const ok = await saveSessionWithRetry(session);
    clearActiveSession();
    setRecoveredSession(null);
    setRecovering(false);
    if (ok) {
      setRecoveryMessage('✓ Session recovered and saved to your Training History');
      try {
        const storedToken = localStorage.getItem('dbpro_session_token');
        const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
        const response = await axios.get(`${API}/sessions/latest`, { headers });
        if (response.data) setLastSession(response.data);
      } catch (err) {
        console.error('Error refreshing last session:', err);
      }
    } else {
      setRecoveryMessage('⚠ Could not sync now — session is stored on this device and will sync automatically');
    }
    setTimeout(() => setRecoveryMessage(null), 6000);
  };

  const handleDiscardSession = () => {
    clearActiveSession();
    setRecoveredSession(null);
  };

  // Navigate to Live Session screen with session config
  const handleStartSession = () => {
    const distance = targetDistance === 'custom' 
      ? (parseInt(customDistance) || 500) 
      : parseInt(targetDistance);
    
    // Save current settings as auto-fill for next session
    localStorage.setItem('dbpro_autofill', JSON.stringify({
      mode,
      targetDistance,
      customDistance,
      autoStopEnabled
    }));

    // Pass session config via navigation state
    navigate('/live-session', { 
      state: {
        mode,
        targetDistance: distance,
        autoStopEnabled,
        autoStart: true
      }
    });
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: BRAND.lightGray }}
      data-testid="training-dashboard"
    >
      {/* Unfinished Session Recovery Dialog */}
      {recoveredSession && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(10, 26, 47, 0.85)' }}
          data-testid="recovery-dialog"
        >
          <div className="w-full max-w-sm rounded-xl p-5 shadow-2xl" style={{ backgroundColor: BRAND.white }}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: BRAND.orange }} />
              <h2 className="text-lg font-bold" style={{ color: BRAND.navyDark }}>
                Unfinished Session Found
              </h2>
            </div>
            <p className="text-sm mb-3" style={{ color: BRAND.darkGray }}>
              Your last session ended unexpectedly (the app was closed while tracking). Progress was auto-saved:
            </p>
            <div className="rounded-lg p-3 mb-4 grid grid-cols-2 gap-3 text-sm" style={{ backgroundColor: BRAND.lightGray }}>
              <div>
                <p style={{ color: BRAND.darkGray }}>Distance</p>
                <p className="font-semibold" style={{ color: BRAND.navyDark }} data-testid="recovery-distance">
                  {formatDistance(recoveredSession.totalDistance || 0)}
                </p>
              </div>
              <div>
                <p style={{ color: BRAND.darkGray }}>Duration</p>
                <p className="font-semibold" style={{ color: BRAND.navyDark }} data-testid="recovery-duration">
                  {formatDuration(recoveredSession.duration || 0)}
                </p>
              </div>
              <div>
                <p style={{ color: BRAND.darkGray }}>Mode</p>
                <p className="font-semibold capitalize" style={{ color: BRAND.navyDark }}>
                  {recoveredSession.mode || 'practice'}
                </p>
              </div>
              <div>
                <p style={{ color: BRAND.darkGray }}>Last saved</p>
                <p className="font-semibold" style={{ color: BRAND.navyDark }}>
                  {recoveredSession.savedAt ? new Date(recoveredSession.savedAt).toLocaleTimeString() : '—'}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDiscardSession}
                disabled={recovering}
                data-testid="recovery-discard-button"
                className="flex-1 h-12 rounded-lg font-semibold border-2 transition-all active:scale-95"
                style={{ borderColor: BRAND.mediumGray, color: BRAND.darkGray, backgroundColor: BRAND.white }}
              >
                Discard
              </button>
              <button
                onClick={handleRecoverSession}
                disabled={recovering}
                data-testid="recovery-save-button"
                className="flex-1 h-12 rounded-lg font-semibold transition-all active:scale-95"
                style={{ backgroundColor: BRAND.orange, color: BRAND.white, opacity: recovering ? 0.7 : 1 }}
              >
                {recovering ? 'Saving...' : 'Recover & Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header 
        className="px-4 py-4 flex items-center justify-between"
        style={{ backgroundColor: BRAND.navyDark }}
        data-testid="dashboard-header"
      >
        <div>
          <img
            src={DBPRO_LOGO_URL}
            alt="DBPRO Logo"
            className="h-12 md:h-14 w-auto object-contain"
            style={{ maxHeight: '56px' }}
            data-testid="dbpro-logo"
          />
          <h1 
            className="mt-2 text-lg md:text-xl font-bold"
            style={{ color: BRAND.white }}
            data-testid="dashboard-subtitle"
          >
            Training Dashboard
          </h1>
        </div>
        
        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
            style={{ backgroundColor: BRAND.navy }}
            data-testid="user-menu-button"
          >
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <User className="w-6 h-6" style={{ color: BRAND.white }} />
            )}
          </button>
          
          {showUserMenu && (
            <div 
              className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg overflow-hidden z-50"
              style={{ backgroundColor: BRAND.navy }}
              data-testid="user-dropdown"
            >
              <div className="px-4 py-3 border-b" style={{ borderColor: BRAND.navyLight }}>
                <p className="text-sm font-medium" style={{ color: BRAND.white }}>{user?.name || 'User'}</p>
                <p className="text-xs" style={{ color: BRAND.mediumGray }}>{user?.email}</p>
              </div>
              <button
                onClick={handleViewHistory}
                className="w-full px-4 py-3 text-left flex items-center gap-2 hover:bg-opacity-80 transition-colors"
                style={{ color: BRAND.white }}
                data-testid="history-nav-button"
              >
                <History className="w-4 h-4" />
                <span className="text-sm">Training History</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 text-left flex items-center gap-2 hover:bg-opacity-80 transition-colors"
                style={{ color: BRAND.orange }}
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content - Setup Only */}
      <main className="flex-1 px-4 py-6 space-y-5">
        
        {/* Recovery result message */}
        {recoveryMessage && (
          <div
            className="rounded-lg p-3 text-sm font-medium"
            style={{
              backgroundColor: recoveryMessage.startsWith('✓') ? '#4CAF5020' : '#FFA00020',
              color: recoveryMessage.startsWith('✓') ? '#2E7D32' : '#B26A00'
            }}
            data-testid="recovery-message"
          >
            {recoveryMessage}
          </div>
        )}

        {/* Select Distance */}
        <section className="bg-white rounded-lg p-4 shadow-sm" data-testid="distance-section">
          <h2 className="text-base font-semibold mb-1" style={{ color: BRAND.navyDark }}>
            Select Distance
          </h2>
          <p className="text-sm mb-3" style={{ color: BRAND.darkGray }}>
            Choose your target distance for this session
          </p>
          
          <Select value={targetDistance} onValueChange={handleDistanceChange}>
            <SelectTrigger 
              className="w-full h-12 text-base border-2 rounded-lg"
              style={{ borderColor: BRAND.mediumGray, color: BRAND.navyDark }}
              data-testid="distance-select-trigger"
            >
              <SelectValue placeholder="Select distance" />
            </SelectTrigger>
            <SelectContent className="bg-white border shadow-lg">
              {DISTANCE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className="text-base py-3 cursor-pointer hover:bg-gray-100"
                  style={{ color: BRAND.navyDark }}
                  data-testid={`distance-option-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {showCustomInput && (
            <input
              type="number"
              value={customDistance}
              onChange={(e) => setCustomDistance(e.target.value)}
              placeholder="Enter distance in meters"
              data-testid="custom-distance-input"
              className="mt-3 w-full rounded-lg px-4 py-3 text-base border-2 focus:outline-none focus:ring-2"
              style={{ borderColor: BRAND.mediumGray, color: BRAND.navyDark }}
            />
          )}
        </section>

        {/* Mode */}
        <section className="bg-white rounded-lg p-4 shadow-sm" data-testid="mode-section">
          <h2 className="text-base font-semibold mb-1" style={{ color: BRAND.navyDark }}>
            Mode
          </h2>
          <p className="text-sm mb-3" style={{ color: BRAND.darkGray }}>
            Select your training mode
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {MODE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isActive = mode === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setMode(opt.value)}
                  data-testid={`mode-${opt.value}`}
                  className="p-4 rounded-lg flex flex-col items-center gap-2 transition-all border-2"
                  style={{
                    backgroundColor: isActive ? BRAND.orange : BRAND.white,
                    borderColor: isActive ? BRAND.orange : BRAND.mediumGray,
                    color: isActive ? BRAND.white : BRAND.navyDark
                  }}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-base font-medium">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Auto-stop */}
        <section className="bg-white rounded-lg p-4 shadow-sm" data-testid="autostop-section">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-5 h-5" style={{ color: BRAND.orange }} />
                <h2 className="text-base font-semibold" style={{ color: BRAND.navyDark }}>
                  Auto-stop
                </h2>
              </div>
              <p className="text-sm" style={{ color: BRAND.darkGray }}>
                Auto-stop ends the session automatically when distance or time goal is reached.
              </p>
            </div>
            <DBPROToggle
              checked={autoStopEnabled}
              onCheckedChange={setAutoStopEnabled}
            />
          </div>
        </section>

        {/* Start Session Button */}
        <button
          type="button"
          onClick={handleStartSession}
          data-testid="start-session-button"
          className="w-4/5 mx-auto block h-14 text-lg font-bold rounded-lg shadow-lg transition-all active:scale-95"
          style={{ backgroundColor: BRAND.orange, color: BRAND.white }}
        >
          Create New Session
        </button>

        {/* Last Session Summary (read-only display) */}
        {lastSession && (
          <section className="bg-white rounded-lg p-4 shadow-sm" data-testid="last-session-section">
            <h2 className="text-base font-semibold mb-3 flex items-center gap-2" style={{ color: BRAND.navyDark }}>
              <CheckCircle className="w-5 h-5" style={{ color: '#4CAF50' }} />
              Last Session
            </h2>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Route className="w-5 h-5" style={{ color: BRAND.mediumGray }} />
                <div>
                  <p className="text-sm" style={{ color: BRAND.darkGray }}>Distance</p>
                  <p className="text-base font-semibold" style={{ color: BRAND.orange }}>
                    {formatDistance(lastSession.distance_covered)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5" style={{ color: BRAND.mediumGray }} />
                <div>
                  <p className="text-sm" style={{ color: BRAND.darkGray }}>Duration</p>
                  <p className="text-base font-semibold" style={{ color: BRAND.orange }}>
                    {formatDuration(lastSession.duration_seconds)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5" style={{ color: BRAND.mediumGray }} />
                <div>
                  <p className="text-sm" style={{ color: BRAND.darkGray }}>Auto-stop</p>
                  <p className="text-base font-semibold" style={{ color: lastSession.auto_stopped ? '#4CAF50' : BRAND.mediumGray }}>
                    {lastSession.auto_stopped ? 'Triggered' : 'Not triggered'}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default TrainingDashboard_DBPRO;
